import React, { useState } from 'react';
import ToggleSwitch from 'components/ToggleSwitch';
import { useVacationMode } from 'hooks/useVacationMode';
import {
  VACATION_DEFAULT_PICK,
  VacationQuickPick,
  formatVacationDate,
  maxReturnDate,
  minReturnDate,
  quickPickDate,
  vacationUntilToDateString,
} from 'utils/vacation';
import './VacationModeCard.css';

type Pick = VacationQuickPick | 'custom' | 'none';

const QUICK_PICKS: { value: VacationQuickPick; label: string }[] = [
  { value: '1w', label: '1 week' },
  { value: '2w', label: '2 weeks' },
  { value: '1m', label: '1 month' },
];

/** What Vacation Mode pauses; the card lists exactly these. */
const PAUSED = ['Sends to your want list', 'New trade offers', 'Have List SMS alerts', 'Matching'];

/**
 * Preferences > Vacation Mode. Off: one row with a toggle. On: an amber card
 * with the return date ("Back in" quick picks, a date input, or no end date)
 * and what is paused. Every change saves at once.
 */
const VacationModeCard: React.FC = () => {
  const vacation = useVacationMode();
  const untilDate = vacationUntilToDateString(vacation.until);
  // Which "Back in" button reads as selected. A stored date that matches a quick
  // pick shows that pick; anything else is a picked date.
  const [explicitPick, setExplicitPick] = useState<Pick | null>(null);
  const derivedPick: Pick = !untilDate
    ? 'none'
    : (QUICK_PICKS.find((p) => quickPickDate(p.value) === untilDate)?.value ?? 'custom');
  const pick = explicitPick ?? derivedPick;

  const handleToggle = (on: boolean) => {
    setExplicitPick(null);
    if (on) vacation.turnOn(quickPickDate(VACATION_DEFAULT_PICK));
    else vacation.turnOff();
  };

  const choose = (next: Pick) => {
    setExplicitPick(next);
    if (next === 'none') vacation.turnOn(null);
    else if (next !== 'custom') vacation.turnOn(quickPickDate(next));
    // 'custom' only reveals the date input; nothing is saved until a date is picked.
  };

  const description = !vacation.active
    ? "Pause new sends and offers while you're away. Your want list stays intact."
    : vacation.until
      ? `On until ${formatVacationDate(vacation.until)} · turns off automatically`
      : 'On until you turn it off';

  return (
    <div className={`vacation-card ${vacation.active ? 'vacation-card--on' : ''}`} data-testid="vacation-card">
      <div className="vacation-card__row">
        <div className="vacation-card__label">
          <div className="vacation-card__title">Vacation Mode</div>
          <div className="vacation-card__desc" data-testid="vacation-card-desc">{description}</div>
        </div>
        <ToggleSwitch
          checked={vacation.active}
          onChange={handleToggle}
          disabled={vacation.isSaving}
          ariaLabel="Vacation Mode"
        />
      </div>

      {vacation.active && (
        <div className="vacation-card__details">
          <div className="vacation-card__group">
            <span className="vacation-card__group-label">Back in</span>
            <div className="vacation-card__picks" role="group" aria-label="Return date">
              {QUICK_PICKS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className={`vacation-pick ${pick === p.value ? 'vacation-pick--active' : ''}`}
                  aria-pressed={pick === p.value}
                  onClick={() => choose(p.value)}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                className={`vacation-pick ${pick === 'custom' ? 'vacation-pick--active' : ''}`}
                aria-pressed={pick === 'custom'}
                onClick={() => choose('custom')}
              >
                Pick date
              </button>
              <button
                type="button"
                className={`vacation-pick ${pick === 'none' ? 'vacation-pick--active' : ''}`}
                aria-pressed={pick === 'none'}
                onClick={() => choose('none')}
              >
                No end date
              </button>
            </div>
            {pick === 'custom' && (
              <input
                type="date"
                className="vacation-card__date"
                aria-label="Return date"
                value={untilDate ?? ''}
                min={minReturnDate()}
                max={maxReturnDate()}
                onChange={(e) => {
                  if (e.target.value) vacation.turnOn(e.target.value);
                }}
              />
            )}
          </div>

          <div className="vacation-card__group">
            <span className="vacation-card__group-label">Paused</span>
            <div className="vacation-card__chips">
              {PAUSED.map((item) => (
                <span key={item} className="vacation-card__chip">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <p className="vacation-card__footnote">
            Your profile shows {vacation.until ? `Away until ${formatVacationDate(vacation.until, { weekday: false })}` : 'Away'}.
            Trades in progress aren't affected.
          </p>
        </div>
      )}
    </div>
  );
};

export default VacationModeCard;
